import React, { useState } from 'react'
import { Button, Card, Grid, makeStyles, Typography } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { vendorFormSchema } from 'schemas/vendor'
import CustomTextField from 'components/Form/CustomTextField'
import {
  Collection,
  SubjectInfoMessages,
  EXHIBITOR_INFO_TEXT,
  subjectReason
} from 'constants/index'
import { Box } from '@mui/material'
import ProgressButton from 'components/ProgressButton'
import { useDispatch, useSelector } from 'react-redux'
import { resetSubject, submitSubjectData, verifyGoogleRecaptcha } from 'store/subject'
import { toast } from 'react-toastify'
import Recaptcha from 'components/Recaptcha'
import { useHistory } from 'react-router-dom'
import SubjectSpecificDetails from 'components/SubjectSpecificDetails'
import AvatarPicker from 'components/AvatarPicker'
import { updateVendorLogo } from 'store/vendor'
import InfoIconAlongText from 'components/InfoIconAlongText'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'

const Exhibitor = (props) => {
  const { subject, submitTitle, handleSubmit, reason } = props
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [recaptchaCode, setRecaptchaCode] = useState('')
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(false)

  const { conference } = useSelector((state) => state.conference)

  const handleChangeRecaptcha = (value) => {
    setRecaptchaCode(value)
    if (value) {
      setIsRecaptchaValid(true)
    }
  }
  const handleExpired = () => {
    setIsRecaptchaValid(false)
  }

  const goBack = () => {
    dispatch(resetSubject())
    history.goBack()
  }

  const initialValues = {
    email: subject?.email ?? '',
    name: subject?.name ?? '',
    booth: subject?.booth ?? '',
    websiteUrl: subject?.websiteUrl ?? '',
    intro: subject?.intro ?? '',
    message: subject?.message ?? ''
  }

  const handleLogoUpload = async (name) => {
    await dispatch(submitSubjectData(subject?.id, { ...subject, logoImage: name }))
    reason === subjectReason.afterApproval && (await updateVendorLogo(subject?.vendorId, name))
  }

  const handleLogoDelete = async () => {
    await dispatch(submitSubjectData(subject?.id, { ...subject, logoImage: null }))
    reason === subjectReason.afterApproval && (await updateVendorLogo(subject?.vendorId, null))
  }

  return (
    <Card className={reason === subjectReason.registration ? classes.card : ''} elevation={0}>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={async (values) => {
          try {
            setLoading(true)
            if (reason === subjectReason.registration) {
              const isVerified = await dispatch(verifyGoogleRecaptcha(recaptchaCode))
              if (isVerified) {
                await handleSubmit(values)
              } else {
                toast.error('reCAPTCHA verification is failed.')
              }
            } else {
              await handleSubmit(values, reason)
            }
          } catch (e) {
            // do nothing
          } finally {
            setLoading(false) // do nothing but stops the loading
          }
        }}
        validationSchema={vendorFormSchema}>
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <Box className={classes.headBar}>
              <Typography variant="h4">
                {reason === subjectReason.forApproval
                  ? 'Review Exhibitor'
                  : reason === subjectReason.afterApproval
                  ? 'Edit Exhibitor'
                  : 'Register as an Exhibitor'}
              </Typography>
              {reason !== subjectReason.registration && (
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  <Button size="small" color="primary" variant="contained" onClick={goBack}>
                    back
                  </Button>
                  <ProgressButton
                    type="submit"
                    label={submitTitle}
                    inProgress={loading}
                    disabled={loading}
                  />
                </Box>
              )}
            </Box>

            {reason !== subjectReason.registration && (
              <Box p={1} border={1} sx={{ mb: '2rem' }}>
                <Box
                  p={1}
                  sx={{
                    backgroundColor: '#ECECEC',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                  <Box className={classes.singleInfo}>
                    <Typography className={classes.label}>Conference Name:</Typography>
                    <Typography>{conference?.confName}</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {reason === subjectReason.registration && (
              <SubjectSpecificDetails infoText={SubjectInfoMessages.EXHIBITOR} />
            )}

            <Grid container spacing={3} className={classes.form}>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Email"
                  name="email"
                  type="email"
                  disabled={reason === subjectReason.registration}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField label="Exhibitor Name" name="name" type="text" />
              </Grid>
              <Grid item xs={12} md={4}>
                <CustomTextField label="Booth No" name="booth" type="text" />
              </Grid>
              <Grid item xs={12} md={8}>
                <CustomTextField label="Website URL" name="websiteUrl" type="text" />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.labelA}>
                    Introduction
                    <InfoIconAlongText infoText={EXHIBITOR_INFO_TEXT.introduction} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.intro}
                    onChange={(value) => setFieldValue('intro', value)}
                    placeholder="Write introduction here..."
                  />
                  <ErrorMessage message={touched?.intro && errors?.intro} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.labelA}>
                    Message
                    <InfoIconAlongText infoText={EXHIBITOR_INFO_TEXT.message} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.message}
                    onChange={(value) => setFieldValue('message', value)}
                    placeholder="Write message here..."
                  />
                  <ErrorMessage message={touched?.message && errors?.message} />
                </Box>
              </Grid>

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Exhibitor Logo"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                  path={Collection.VENDORS}
                  onUpload={handleLogoUpload}
                  onDelete={handleLogoDelete}
                  imageName={subject?.logoImage ? subject?.logoImage : null}
                  resetImage={!subject?.imageName}
                  conferenceId={subject?.conferenceId}
                  infoText={EXHIBITOR_INFO_TEXT.logo}
                  isSubject={true}
                  reason={reason}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box className={classes.buttonBox}>
                {reason === subjectReason.registration && (
                  <Recaptcha handleChange={handleChangeRecaptcha} onExpired={handleExpired} />
                )}
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  {reason !== subjectReason.registration && (
                    <Button onClick={goBack}>Cancel</Button>
                  )}
                  <ProgressButton
                    type="submit"
                    label={submitTitle}
                    inProgress={loading}
                    disabled={
                      loading || (reason === subjectReason.registration && !isRecaptchaValid)
                    }
                  />
                </Box>
              </Box>
            </Grid>
          </Form>
        )}
      </Formik>
    </Card>
  )
}

export default Exhibitor

const useStyles = makeStyles({
  labelA: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  },
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0rem 0rem 2rem 0rem'
  },
  card: {
    padding: '1rem',
    border: '1px solid #e0e0e0'
  },
  header: {
    fontWeight: 'bold'
  },
  form: {
    marginTop: '1.5rem'
  },
  singleInfo: {
    display: 'flex',
    gap: '2rem'
  },
  label: {
    minWidth: '132px',
    fontSize: '15px',
    fontWeight: '600'
  },
  buttonBox: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1rem'
  }
})
